/**
 * Client Axios partagé : base URL, intercepteurs (token, cache, erreurs).
 * Utilisé par tous les modules API.
 */
import axios from 'axios'
import { getApiBaseUrl } from '../../utils/platform'
import { getCachedData, setCachedData } from '../../utils/cache'
import { isAbortError } from '../../utils/abort'

export const API_BASE_URL = getApiBaseUrl()

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

api.interceptors.request.use(
  (config) => {
    // Ne pas écraser un token passé par l'appelant (ex. juste après login sur mobile, où localStorage peut être indisponible)
    if (!config.headers.Authorization) {
      try {
        const token = localStorage.getItem('token')
        if (token) config.headers.Authorization = `Bearer ${token}`
      } catch (e) {
        // Sur certains mobiles (mode privé, stockage désactivé), localStorage peut lever
      }
    }
    const urlPath = (config.url || '').replace(/^https?:\/\/[^/]+/, '') || config.url
    if (config.method === 'get' && config.useCache !== false) {
      const params = config.params ? Object.fromEntries(new URLSearchParams(config.params)) : {}
      const cached = getCachedData(urlPath, params)
      if (cached) {
        if (import.meta.env.DEV) console.log(`💾 Cache hit: ${urlPath}`, params)
        return Promise.reject({
          __fromCache: true,
          data: cached,
          config,
          status: 200,
          statusText: 'OK (from cache)',
        })
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

api.interceptors.response.use(
  (response) => {
    const config = response.config
    const urlPath = (config.url || '').replace(/^https?:\/\/[^/]+/, '') || config.url
    if (config.method === 'get' && config.useCache !== false && response.status === 200) {
      const params = config.params ? Object.fromEntries(new URLSearchParams(config.params)) : {}
      const cacheDuration = urlPath.includes('/parametres') ? 10 * 60 * 1000 : 5 * 60 * 1000
      setCachedData(urlPath, params, response.data, cacheDuration)
    }
    return response
  },
  (error) => {
    if (error.__fromCache) {
      return Promise.resolve({
        data: error.data,
        status: error.status,
        statusText: error.statusText,
        config: error.config,
        headers: {},
        __fromCache: true,
      })
    }
    if (isAbortError(error)) {
      return Promise.reject(error)
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
      const apiUrl = API_BASE_URL
      const isAndroid = navigator.userAgent.includes('OrdonnanceurApp Mobile')
      const isRemote = /^https?:\/\//.test(apiUrl) && !apiUrl.includes('localhost')
      let errorMessage = 'Erreur de connexion réseau.\n\n'
      errorMessage += `URL tentée: ${apiUrl}\n\n`
      if (isAndroid) {
        errorMessage += 'Vérifiez que le backend est lancé, même réseau WiFi, pare-feu, etc.'
      } else if (isRemote) {
        errorMessage += 'Backend distant injoignable. Vérifiez :\n- que l’URL est correcte et le backend déployé (ex. ouvrir /api/health dans le navigateur)\n- CORS : dans le projet backend Vercel, ALLOWED_ORIGINS doit contenir l’origine du front (ex. https://financeprevision-frontend.vercel.app), puis redéployer le backend'
      } else {
        errorMessage += 'Vérifiez que le backend est lancé sur http://localhost:5000'
      }
      const networkError = new Error(errorMessage)
      networkError.isNetworkError = true
      networkError.apiUrl = apiUrl
      return Promise.reject(networkError)
    }
    return Promise.reject(error)
  }
)

export default api
